import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Pencil, Plus, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../../layouts/PageHeader';
import { Button } from '../../../common/Button';
import type { Subscription, Invoice } from '../../../../types/subscriptionManagement';
import { InvoiceTable } from './InvoiceTable';
import { InvoiceFormModal } from './InvoiceFormModal';
import { InvoiceViewerModal } from './InvoiceViewerModal';
import { DeleteConfirmModal } from '../DeleteConfirmModal';
import { loadFxRates, toInr, getFxRates } from '../../../../utils/fxUtils';
import {
  avgInvoiceInSubscriptionCurrency,
  totalSpentInSubscriptionCurrency,
} from '../../../../utils/subscriptionInvoiceRollup';
import { effectiveRenewalDate } from '../../../../utils/subscriptionRenewal';
import { isPaidInvoiceStatus } from '../../../../utils/subscriptionInvoiceCoverage';
import { approvedBudgetCapThroughLatestPaid } from '../../../../utils/subscriptionFyUtils';

interface Props {
  subscription: Subscription;
  /** For invoice Subscription ID dropdown + name/currency resolution. */
  allSubscriptions: Subscription[];
  /** Distinct payment method names (same source as subscription form). */
  paymentMethodNames: string[];
  lookupColumns?: Record<string, string[]>;
  refreshSnapshot?: () => void;
  /** All invoices for this subscription (renewal logic, edit lookup). */
  invoices: Invoice[];
  saving: boolean;
  onBack: () => void;
  onEdit: () => void;
  onCreateInvoice: (data: Partial<Invoice>) => Promise<Invoice>;
  onUpdateInvoice: (id: string, data: Partial<Invoice>) => Promise<Invoice>;
  onDeleteInvoice: (id: string) => Promise<void>;
  onUploadFile: (id: string, file: File) => Promise<string>;
}

function fmtAmt(n: number | string, currency?: string): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const prefix = currency === 'INR' ? '₹' : currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  return `${prefix}${v.toLocaleString('en-US')}`;
}

function fmtMoneyDetail(n: number, currency?: string, maxFractionDigits = 2): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const prefix = currency === 'INR' ? '₹' : currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  return `${prefix}${v.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  })}`;
}

function fmtDate(d: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-slate-800">{value || '—'}</p>
  </div>
);

const STATUS_COLORS: Record<string, string> = {
  Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Paused: 'bg-amber-50 text-amber-700 border-amber-200',
  Cancelled: 'bg-red-50 text-red-700 border-red-200',
};

export const SubscriptionDetailScreen: React.FC<Props> = ({
  subscription: sub,
  allSubscriptions,
  paymentMethodNames,
  lookupColumns,
  refreshSnapshot,
  invoices,
  saving,
  onBack,
  onEdit,
  onCreateInvoice,
  onUpdateInvoice,
  onDeleteInvoice,
  onUploadFile,
}) => {
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);

  // Load FX rates for expense comparison
  useEffect(() => { loadFxRates(); }, []);

  const invoiceSpendRollup = useMemo(() => {
    const ccy = sub.currency || 'USD';
    const total = totalSpentInSubscriptionCurrency(invoices, ccy);
    const avg = avgInvoiceInSubscriptionCurrency(invoices, ccy);
    return { total, avg, count: invoices.length };
  }, [invoices, sub.currency]);

  const displayRenewal = useMemo(
    () => effectiveRenewalDate(sub, invoices),
    [sub, invoices],
  );

  // Paid INR vs approved cap from contract start through latest paid date (calendar months, not FY)
  const expenseComparison = useMemo(() => {
    const paid = invoices.filter(i => isPaidInvoiceStatus(i.paymentStatus));
    const paidInr = paid.reduce(
      (s, i) =>
        s +
        toInr(Number(i.amount) || 0, i.currency || 'INR', {
          invoiceDate: i.invoiceDate,
        }),
      0,
    );
    const approvedCapSub = approvedBudgetCapThroughLatestPaid(sub, invoices);
    const approvedInr = toInr(approvedCapSub, sub.currency || 'INR');
    return { paidInr, approvedInr, over: paidInr > approvedInr, paidCount: paid.length };
  }, [invoices, sub]);

  function fmtInr(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 10_000_000) {
      return `${sign}₹${(abs / 10_000_000).toFixed(2)}Cr`;
    }
    if (abs >= 100_000) {
      return `${sign}₹${(abs / 100_000).toFixed(2)}L`;
    }
    if (abs >= 1_000) {
      return `${sign}₹${(abs / 1_000).toFixed(2)}K`;
    }
    return `${sign}₹${abs.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }


  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [deleteInvoice, setDeleteInvoice] = useState<Invoice | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);

  const handleAddInvoice = () => { setEditInvoice(null); setInvoiceFormOpen(true); };
  const handleEditInvoice = (inv: Invoice) => { setEditInvoice(inv); setInvoiceFormOpen(true); };

  const subscriptionOptions = allSubscriptions.map(s => ({
    id: s.id,
    name: s.name,
    currency: s.currency || 'USD',
    paymentMethod: s.paymentMethod || '',
    billingCycle: s.billingCycle || 'Monthly',
  }));

  const handleSaveInvoice = async (
    data: Partial<Invoice>,
    opts?: { keepOpen?: boolean },
  ): Promise<Invoice | undefined> => {
    const sid = String(data.subscriptionId || sub.id).trim();
    const target = allSubscriptions.find(s => s.id === sid) || sub;
    const payload = {
      ...data,
      subscriptionId: sid,
      currency:
        (data.currency && String(data.currency).trim()) ||
        target.currency ||
        'USD',
    };
    // Add-invoice modal can hold `form.id` after upload (keepOpen). If the user
    // then enters a different invoice number, we must CREATE — not PATCH the
    // previous row (which looked like “no new row / no error”).
    const num = String(data.invoiceNumber || '').trim();
    let targetId: string | undefined;
    if (editInvoice?.id) {
      targetId = editInvoice.id;
    } else if (data.id) {
      const existing = invoices.find(i => i.id === data.id);
      if (
        existing &&
        String(existing.invoiceNumber || '').trim() === num
      ) {
        targetId = data.id;
      } else if (!existing && num) {
        // Row was just created (e.g. save with keepOpen before file upload); list may not have revalidated yet.
        targetId = data.id;
      }
    }
    let saved: Invoice | undefined;
    if (targetId) {
      saved = await onUpdateInvoice(targetId, payload);
    } else {
      saved = await onCreateInvoice(payload);
    }
    if (!opts?.keepOpen) {
      setInvoiceFormOpen(false);
      setEditInvoice(null);
    }
    return saved;
  };

  const handleConfirmDeleteInvoice = async () => {
    if (!deleteInvoice) return;
    await onDeleteInvoice(deleteInvoice.id);
    setDeleteInvoice(null);
  };

  const headerContent = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0 w-full">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Button variant="outline" size="sm" type="button" onClick={onBack}>
          <ArrowLeft size={13} /> Back
        </Button>
        <h1 className="text-base font-semibold text-slate-900 truncate min-w-0">{sub.name}</h1>
        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border shrink-0 ${STATUS_COLORS[sub.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
          {sub.status}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader>{headerContent}</PageHeader>

      <div className="p-6 space-y-5">
        {/* Overview Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-1 h-4 rounded-full bg-blue-500 shrink-0" />
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Subscription Details
              </p>
              <span className="text-[10px] font-semibold text-slate-400">
                All-period
              </span>
            </div>
            <button
              type="button"
              onClick={onEdit}
              className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Pencil size={13} /> Edit
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-4">
            <InfoItem label="Vendor" value={sub.vendor} />
            <InfoItem label="Department" value={sub.department} />
            <InfoItem label="Category" value={sub.category} />
            <InfoItem label="Owner" value={sub.owner} />
            <InfoItem label="Approved By" value={sub.approvedBy} />
            <InfoItem label="Billing Cycle" value={sub.billingCycle} />
            <InfoItem label="Approved Amount" value={fmtAmt(sub.amount, sub.currency)} />
            <InfoItem
              label="Total Spent"
              value={fmtMoneyDetail(invoiceSpendRollup.total, sub.currency, 2)}
            />
            <InfoItem
              label="Avg / invoice"
              value={
                invoiceSpendRollup.count > 0
                  ? `${fmtMoneyDetail(invoiceSpendRollup.avg, sub.currency, 2)}`
                  : '—'
              }
            />
            <InfoItem label="Currency" value={sub.currency} />
            <InfoItem label="Start Date" value={fmtDate(sub.startDate)} />
            <InfoItem label="Renewal Date" value={fmtDate(displayRenewal)} />
            <InfoItem label="Payment Method" value={sub.paymentMethod} />
            <InfoItem label="Approved Budget" value={sub.approvedBudget} />
            <InfoItem label="Status" value={sub.status} />
            <InfoItem label="Approval Status" value={sub.approvalStatus} />
          </div>
          {/* Expense vs Approved */}
          {expenseComparison.paidCount > 0 && (
            <div className={`mt-4 flex flex-wrap items-center gap-3 border-t pt-3 ${
              expenseComparison.over ? 'border-red-100' : 'border-slate-100'
            }`}>
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${
                expenseComparison.over
                  ? 'bg-red-50 border border-red-200 text-red-700'
                  : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              }`}>
                {expenseComparison.over && <AlertTriangle size={13} className="shrink-0" />}
                <span>
                  Actual spend: <span className="font-bold">{fmtInr(expenseComparison.paidInr)}</span>
                  {' '}/ Approved: <span className="font-bold">{fmtInr(expenseComparison.approvedInr)}</span>
                </span>
                {expenseComparison.over && (
                  <span className="ml-1 text-red-600">
                    (+{fmtInr(expenseComparison.paidInr - expenseComparison.approvedInr)} over)
                  </span>
                )}
              </div>
              <span className="text-[10px] text-slate-400">
                FX: USD ₹{getFxRates().USD} · GBP ₹{getFxRates().GBP} · EUR ₹{getFxRates().EUR}
              </span>
            </div>
          )}

          {sub.notes && (
            <p className="mt-4 text-sm text-slate-500 border-t border-slate-100 pt-3">{sub.notes}</p>
          )}
        </div>

        {/* Invoices Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-violet-500 shrink-0" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Invoices ({invoices.length})
            </h3>
          </div>
          <Button variant="primary" size="sm" type="button" onClick={handleAddInvoice}>
            <Plus size={14} /> Add Invoice
          </Button>
        </div>

        <InvoiceTable
          invoices={invoices}
          onEdit={handleEditInvoice}
          onDelete={inv => setDeleteInvoice(inv)}
          onUpload={onUploadFile}
          onViewFile={url => setViewerUrl(url)}
        />
      </div>

      {/* Modals */}
      <InvoiceFormModal
        isOpen={invoiceFormOpen}
        onClose={() => { setInvoiceFormOpen(false); setEditInvoice(null); }}
        onSave={handleSaveInvoice}
        invoice={editInvoice}
        subscriptionOptions={subscriptionOptions}
        paymentMethodNames={paymentMethodNames}
        lookupColumns={lookupColumns}
        refreshSnapshot={refreshSnapshot}
        defaultSubscriptionId={sub.id}
        saving={saving}
        onUploadFile={onUploadFile}
      />

      <DeleteConfirmModal
        isOpen={!!deleteInvoice}
        onClose={() => setDeleteInvoice(null)}
        onConfirm={handleConfirmDeleteInvoice}
        title="Delete Invoice"
        message={`Delete invoice "${deleteInvoice?.invoiceNumber}"? This cannot be undone.`}
        saving={saving}
      />

      <InvoiceViewerModal
        isOpen={!!viewerUrl}
        onClose={() => setViewerUrl(null)}
        url={viewerUrl || ''}
      />
    </>
  );
};
